import { useEffect, useRef, useState, useCallback } from "react";
import { Outlet, useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { useApiService } from "../shared/apiservice";
import { useEcho } from "../shared/echo";
import { AuditLogEntry } from "../shared/types";
import DashboardLayout from "./layouts/dashboard";
import { ProviderStockWarnings, useStockWarnings } from "../api/stockwarnings";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import { Chip, Table, TableRow } from "@mui/material";
import TimeAgo from 'react-time-ago';
import "react-time-ago/locale/en"
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import { DangerousOutlined, InfoOutline, InfoOutlined, WarningOutlined } from "@mui/icons-material";

export default function Dashboard() {
    return (
        <DashboardLayout>
            <ProviderStockWarnings>
                <DashboardContent />
            </ProviderStockWarnings>
        </DashboardLayout>
    );
}

function DashboardContent() {
    const navigate = useNavigate();
    const apiService = useApiService();
    const echo = useEcho();

    const [entries, setEntries] = useState<AuditLogEntry[]>([]);
    const { stockExpiryWarnings, loading: stockExpiryWarningsLoading, total: stockExpiryWarningsTotal, setPerPage: setStockExpiryWarningsPerPage } = useStockWarnings();
    const [loading, setLoading] = useState(true);

    const fetchEntries = useCallback(async (silent?: boolean) => {
        if (!silent) setLoading(true);
        try {
            const params = new URLSearchParams({
                page: "1",
                per_page: "10",
                sort: "created_at",
                order: "desc",
            });
            const response = await apiService.get(`/audit/log?${params.toString()}`);
            if (response.ok) {
                const json = await response.json();
                const data: AuditLogEntry[] = (json.data ?? json).map((d: any) => ({
                    ...d,
                    created_at: new Date(d.created_at),
                }));
                setEntries(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setStockExpiryWarningsPerPage(10);
        fetchEntries();
    }, [fetchEntries]);

    const fetchRef = useRef(fetchEntries);
    fetchRef.current = fetchEntries;

    useEffect(() => {
        if (!echo) return;

        const channel = echo.private("warehouse");
        const reload = () => fetchRef.current(true);

        channel.listen(".audit.created", reload);

        return () => {
            channel.stopListening(".audit.created", reload);
        };
    }, [echo]);

    function _getWarningIcon(warningLevel: number): React.ReactNode {
        switch (warningLevel) {
            case 1:
                return <InfoOutlined fontSize="small" color="primary" />;
            case 2:
                return <WarningOutlined fontSize="small" color="warning" />;
            case 3:
                return <DangerousOutlined fontSize="small" color="error" />;
        }
        return null;
    }

    return (
        <Stack direction="column" justifyContent="stretch" alignItems="stretch" spacing={3}>
            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Stock Expiry Warnings ({stockExpiryWarningsTotal})
                        </Typography>
                        <Button
                            size="small"
                            endIcon={<VisibilityRoundedIcon fontSize="small" />}
                            onClick={() => navigate("/stock/warnings/expiry")}
                        >
                            View all
                        </Button>
                    </Stack>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Code</TableCell>
                                <TableCell>Product</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Level</TableCell>
                                <TableCell>Expires</TableCell>
                                <TableCell align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stockExpiryWarningsLoading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                    </TableRow>
                                ))
                            ) : stockExpiryWarnings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                            No current stock expiry warnings.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                stockExpiryWarnings.map((warning) => (
                                    <TableRow key={warning.stock.id}>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Box component="span" sx={{ color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: '50%' }}>
                                                    {_getWarningIcon(warning.warning_level)}
                                                </Box>
                                                <Typography variant="body2" fontWeight={600}>{warning.stock.product.code}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{warning.stock.product.name}</TableCell>
                                        <TableCell>{warning.stock.location.name}</TableCell>
                                        <TableCell>{warning.stock.level}</TableCell>
                                        <TableCell>
                                            {warning.expired ? (
                                                <Chip label={warning.expires_at != null ? <TimeAgo date={warning.expires_at} locale="en" /> : '-'} color="error" />
                                            ) : warning.warning_level === 1 ? (
                                                <Chip label={warning.expires_at != null ? <TimeAgo date={warning.expires_at} locale="en" /> : '-'} color="warning" />
                                            ) : (
                                                <Chip label={warning.expires_at != null ? <TimeAgo date={warning.expires_at} locale="en" /> : '-'} color="success" />
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                onClick={() => navigate(`/products/${warning.stock.product.id}`)}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Recent Activity
                        </Typography>
                        <Button
                            size="small"
                            endIcon={<VisibilityRoundedIcon fontSize="small" />}
                            onClick={() => navigate("/audit")}
                        >
                            View all
                        </Button>
                    </Stack>

                    {loading ? (
                        <Stack spacing={2} divider={<Divider flexItem />}>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton variant="text" width="60%" />
                                    </Box>
                                    <Skeleton variant="text" width={120} />
                                </Stack>
                            ))}
                        </Stack>
                    ) : entries.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                            No recent activity.
                        </Typography>
                    ) : (
                        <Stack spacing={0} divider={<Divider flexItem />}>
                            {entries.map((entry) => (
                                <Stack
                                    key={entry.id}
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{
                                        py: 1.5,
                                        cursor: "pointer",
                                        "&:hover": { bgcolor: "action.hover" },
                                        borderRadius: 1,
                                        px: 1,
                                        mx: -1,
                                    }}
                                    onClick={() => navigate(`/audit/${entry.id}`)}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" noWrap>
                                            <Typography component="span" variant="body2" fontWeight={600}>
                                                {entry.user.name}
                                            </Typography>
                                            {" — "}
                                            <Typography component="span" variant="body2" sx={{ textTransform: "capitalize" }}>
                                                {entry.event}
                                            </Typography>
                                            {entry.display_type && (
                                                <Typography component="span" variant="body2" color="text.secondary">
                                                    {" · "}{entry.display_type}
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2, flexShrink: 0 }}>
                                        {<TimeAgo date={entry.created_at} locale="en" />}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    )}
                </CardContent>
            </Card>
            <Outlet />
        </Stack >
    );
}
